import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { articles } from "../lib/content";
import { AdSlot } from "../components/AdSlot";

const setMetaTag = (name, content) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
};

const setOg = (property, content) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
};

const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

export const ArticlePage = () => {
    const { slug } = useParams();
    const article = articles.find((a) => a.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!article) return;

        const baseTitle = `${article.title} — Hydration Hub`;
        document.title = baseTitle;
        setMetaTag("description", article.metaDescription || article.excerpt);
        setOg("og:title", article.title);
        setOg("og:description", article.metaDescription || article.excerpt);
        setOg("og:type", "article");
        setOg("og:image", article.cover);
        setOg("og:url", `${window.location.origin}/blog/${article.slug}`);

        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `${window.location.origin}/blog/${article.slug}`);

        // JSON-LD article schema for SEO
        const jsonld = {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.metaDescription || article.excerpt,
            image: article.cover,
            datePublished: article.publishedAt,
            author: { "@type": "Organization", name: article.author || "Hydration Hub" },
            publisher: {
                "@type": "Organization",
                name: "Hydration Hub",
            },
        };
        let scriptTag = document.getElementById("article-jsonld");
        if (!scriptTag) {
            scriptTag = document.createElement("script");
            scriptTag.type = "application/ld+json";
            scriptTag.id = "article-jsonld";
            document.head.appendChild(scriptTag);
        }
        scriptTag.textContent = JSON.stringify(jsonld);

        return () => {
            document.title = "Hydration Hub — Daily Water Intake Calculator (Free)";
        };
    }, [slug, article]);

    if (!article) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="article-not-found">
                <div className="editorial-label mb-3">404</div>
                <h1 className="text-5xl font-black tracking-tighter mb-6">Article not found.</h1>
                <Link to="/blog" className="btn-outline-editorial">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to articles
                </Link>
            </div>
        );
    }

    return (
        <article className="border-b border-foreground" data-testid="article-page">
            <header className="border-b border-foreground py-12 sm:py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <Link to="/blog" data-testid="back-to-articles" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-foreground/60 hover:text-primary mb-8">
                        <ArrowLeft className="h-3 w-3" /> All articles
                    </Link>
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary border border-primary px-2 py-1">
                            {article.category}
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">
                            {article.readTime}
                        </span>
                        {article.publishedAt && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">
                                · {formatDate(article.publishedAt)}
                            </span>
                        )}
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-black tracking-tighter leading-[1.05] mb-6">
                        {article.title}
                    </h1>
                    <p className="text-lg text-foreground/70 leading-relaxed">{article.excerpt}</p>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
                <div className="aspect-[16/9] border border-foreground overflow-hidden bg-secondary">
                    <img src={article.cover} alt={article.title} className="w-full h-full object-cover" />
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
                {article.keyTakeaways && article.keyTakeaways.length > 0 && (
                    <aside className="border border-foreground bg-card p-6 sm:p-8 mb-12 hard-shadow-sm" data-testid="key-takeaways">
                        <div className="editorial-label mb-3">Key takeaways</div>
                        <ul className="space-y-3">
                            {article.keyTakeaways.map((kt, i) => (
                                <li key={i} className="flex gap-3 text-base text-foreground/85 leading-relaxed">
                                    <span className="text-primary font-black mt-0.5">→</span>
                                    <span>{kt}</span>
                                </li>
                            ))}
                        </ul>
                    </aside>
                )}

                {article.body.map((block, i) => {
                    if (block.type === "h2") {
                        return (
                            <h2 key={i} className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-4">
                                {block.text}
                            </h2>
                        );
                    }
                    if (block.type === "h3") {
                        return (
                            <h3 key={i} className="text-xl font-bold tracking-tight mt-8 mb-3">
                                {block.text}
                            </h3>
                        );
                    }
                    if (block.type === "ul") {
                        return (
                            <ul key={i} className="space-y-2 mb-6 pl-0">
                                {block.items.map((item, j) => (
                                    <li key={j} className="flex gap-3 text-lg text-foreground/85 leading-relaxed">
                                        <span className="text-primary font-black mt-1 text-sm">▪</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        );
                    }
                    return (
                        <p key={i} className="text-lg text-foreground/85 leading-relaxed mb-6">
                            {block.text}
                        </p>
                    );
                })}

                <AdSlot slot="article-bottom" height={150} testId="ad-article-bottom" />
                <div className="mt-10 p-6 border border-foreground bg-secondary/50">
                    <div className="editorial-label mb-2">Disclaimer</div>
                    <p className="text-sm text-foreground/70">
                        This article is for educational purposes and is not medical advice.
                        Consult a physician for medical concerns.
                    </p>
                </div>
            </div>
        </article>
    );
};
